/**
 * PB-Profil — Vollbild-Edit-View
 *
 * Lädt einen Pflegebedürftigen (V1User mit Profile) + alle profile_options,
 * rendert 9 strukturierte Sections mit Sticky-Sprung-Nav, Scroll-Spy und
 * Sticky-Save-Bar. Speichern persistiert an
 *   PUT /v1/admin/user/{id}
 *   PUT /v1/admin/profile/{id}
 * Vertragsanpassung und Ausgleichsbetrag speichern eigenständig (eigener
 * Button in der Vertrag-Sektion), nicht über diesen Save.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Button, Card } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { toLocalDateKey } from '../../../lib/localDate';
import { formatTime } from '../../../lib/dateFormat';
import {
  v1ProfileOptions, v1Users, v1Profiles, v1Admins,
  type V1ProfileOption, type V1User, type V1Profile, primaryProfile,
} from '../../../lib/v1';
import { v2Members, type FamilyMemberInput } from '../../../lib/familyAccounts';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { HARDCODED } from './options';
import { Select, Text } from './controls';
import { useOptionLabel } from '../../../lib/useOptionLabel';
import { SECTIONS_NAV, SLIM_SECTION_IDS, FormSections, deriveSectionPerms, selectedRoles, type PbFormState } from './sections';
import { formRequiredContext, missingFieldLabels, validateProfile } from './validation';
import { flushPendingVertrag } from './pendingVertrag';
import { freezeSektionenAktiv, inactiveSectionFields, isSectionActive, parseSektionenAktiv } from './sectionToggles';
import { USER_FIELDS, PROFILE_FIELDS, pickPayload } from './fields';
import { toTimeInput, type TerminField } from '../../../lib/terminFields';

interface Props {
  user: V1User | null;          // null = "new client" mode
  onClose: () => void;
  /** Called after a successful save with the updated user (incl. profile). */
  onSaved?: (u: V1User) => void;
  /**
   * Paket A (A9): false = Nicht-Pflegeempfänger (Kontaktperson etc.). Dann
   * werden nur „Profil" + „Stammdaten" gezeigt, solange der Schalter
   * `is_pflegeempfaenger` im Formular nicht aktiviert wird.
   */
  careRecipient?: boolean;
  dark: boolean;
  /** When true the component skips the breadcrumb + summary card (the
   *  outer PbDetailPage owns those). The form itself + sticky section nav
   *  + save bar still render. */
  embedded?: boolean;
}

/* Map V1User + V1Profile → flat PbFormState the form works on. */
function userToForm(user: V1User | null): PbFormState {
  if (!user) return {};
  const p: V1Profile | null = primaryProfile(user);
  const safeStr = (v: unknown): string | undefined => (v == null ? undefined : String(v));
  const safeArr = (v: unknown): string[] | undefined => {
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.startsWith('[')) {
      try { const parsed = JSON.parse(v); if (Array.isArray(parsed)) return parsed.map(String); } catch { /* ignore */ }
    }
    return undefined;
  };
  /** Like safeArr but also splits legacy comma-separated strings so old
   *  single-value rows (stored as plain VARCHAR) load as a one-element array. */
  const safeArrWithCsvFallback = (v: unknown): string[] => {
    const arr = safeArr(v);
    if (arr !== undefined) return arr;
    if (typeof v === 'string' && v.trim()) {
      return v.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };
  // Abgeleitete Sektions-Schalter beim Laden festschreiben — sonst schaltet
  // das Leeren des letzten Felds einer Sektion diese aus und das Leeren wird
  // nicht gespeichert (inaktive Sektionen fallen aus dem Payload).
  return freezeSektionenAktiv({
    // user-level
    status: safeStr(user.status),
    konto: safeStr(user.konto),
    startdatum: safeStr(user.startdatum) ?? '',
    // Datums-Casts kommen (je Endpunkt) entweder schon als reines Y-m-d oder
    // noch als ISO-Timestamp mit Z/Offset. Ein rohes `.slice(0, 10)` schneidet
    // im zweiten Fall den UTC-Tag ab, nicht den lokalen — Mitternacht
    // Europe/Berlin liegt in UTC am Vortag (Bug E2E Staging 22.09.2026,
    // anamnese_termin_am zeigte nach Reload einen Tag zu früh). toLocalDateKey
    // lässt reine Y-m-d-Strings unverändert und rechnet Datetime-Strings über
    // die lokalen Date-Komponenten um (siehe lib/localDate.ts).
    zusage_erhalten_am: toLocalDateKey(safeStr((user as Record<string, unknown>).zusage_erhalten_am) ?? ''),
    mustervertrag_versendet_am: toLocalDateKey(safeStr((user as Record<string, unknown>).mustervertrag_versendet_am) ?? ''),
    anamnese_termin_am: toLocalDateKey(safeStr((user as Record<string, unknown>).anamnese_termin_am) ?? ''),
    beratungsgespraech_am: toLocalDateKey(safeStr((user as Record<string, unknown>).beratungsgespraech_am) ?? ''),
    // TIME-Spalten kommen als "HH:MM:SS" — <input type="time"> will "HH:MM".
    anamnese_termin_um: toTimeInput((user as Record<string, unknown>).anamnese_termin_um),
    beratungsgespraech_um: toTimeInput((user as Record<string, unknown>).beratungsgespraech_um),
    assigned_to: safeStr(user.assigned_to as unknown),
    group_id: safeStr(user.group_id as unknown),
    locale: safeStr(user.locale as unknown) ?? 'de',
    badge: safeStr(user.badge as unknown),
    impfstatus: safeStr(user.impfstatus as unknown),
    rating: typeof user.rating === 'number' ? user.rating : Number(user.rating ?? 0),
    german_skills: safeStr(user.german_skills as unknown),
    description: safeStr(user.description as unknown),
    invisible: Boolean(user.invisible),
    created_by: safeStr(user.created_by as unknown),
    origin: safeStr(user.origin as unknown),
    email: safeStr(user.email),
    mobile: safeStr(user.mobile),
    telefon: safeStr(user.telefon as unknown),
    strasse: safeStr(user.strasse as unknown),
    hausnummer: safeStr(user.hausnummer as unknown),
    plz: safeStr(user.plz as unknown),
    ort: safeStr(user.ort as unknown),
    land: safeStr(user.land as unknown),
    todestag: safeStr(user.todestag as unknown),
    kontoinhaber: safeStr(user.kontoinhaber as unknown),
    bankname: safeStr(user.bankname as unknown),
    iban: safeStr(user.iban as unknown),
    wage: safeStr(user.wage as unknown),
    // Nur lesend in der Termine-Sektion (s. sections.tsx TermineSection) —
    // die eigentliche Wiedervorlage-Pflege läuft über eine eigene Maske mit
    // Uhrzeit (PATCH /v2/admin/users/{id}/wiedervorlage, s. v1.ts). Gleicher
    // toLocalDateKey-Konverter wie bei den anderen Terminfeldern (s.o.), sonst
    // zeigt ein Datetime-String hier den UTC- statt den lokalen Tag.
    wiedervorlage_am: (() => {
      const raw = safeStr((user as Record<string, unknown>).wiedervorlage_am);
      return raw ? toLocalDateKey(raw) : null;
    })(),
    // profile-level
    // Familienkonto-Rolle (Label-Strings + Haushalt-Bool).
    // familiengrad ist jetzt ein string[] (Multi-Select); legacy V1-Profile
    // können noch einen einzelnen String liefern → in Array konvertieren.
    profiltyp: safeStr(p?.profiltyp as unknown),
    // Feedback 11.10.2026 — weitere Rollen (v1 liefert die neue Spalte mit).
    profiltypen: Array.isArray((p as Record<string, unknown> | undefined)?.profiltypen)
      ? ((p as Record<string, unknown>).profiltypen as string[])
      : undefined,
    familiengrad: Array.isArray(p?.familiengrad)
      ? (p!.familiengrad as unknown as string[])
      : (p?.familiengrad ? [safeStr(p.familiengrad as unknown) as string] : []),
    haushalt: Boolean(p?.haushalt),
    // WP2-Schalter: gespeicherten Zustand hydrieren, sonst würde ein bestehender
    // Pflegeempfänger beim Öffnen als Kontakt (Toggle aus) erscheinen und beim
    // Speichern fälschlich herabgestuft werden.
    is_pflegeempfaenger: Boolean(p?.is_pflegeempfaenger),
    anrede: safeStr(p?.anrede),
    titel: safeStr(p?.titel),
    vorname: safeStr(p?.vorname),
    nachname: safeStr(p?.nachname),
    geburtsdatum: safeStr(p?.geburtsdatum),
    nationalitaet: safeStr(p?.nationalitaet as unknown),
    groesse: safeStr(p?.groesse as unknown),
    gewicht: safeStr(p?.gewicht as unknown),
    familienstand: safeStr(p?.familienstand as unknown),
    raucher: safeStr(p?.raucher as unknown),
    krankenkasse: safeStr(p?.krankenkasse as unknown),
    krankenkasse_typ: safeStr(p?.krankenkasse_typ as unknown),
    kranken_vnr: safeStr(p?.kranken_vnr as unknown),
    sozial_vnr: safeStr(p?.sozial_vnr as unknown),
    renten_vnr: safeStr(p?.renten_vnr as unknown),
    steuer_id: safeStr(p?.steuer_id as unknown),
    lbnr: safeStr(p?.lbnr as unknown),
    sozialamt_aufstockung: Boolean(p?.sozialamt_aufstockung),
    // Batch-12 — „04 Pflegedienst" (SGB5)
    sgb5: safeStr((p as Record<string, unknown> | null)?.sgb5),
    sgb5_medigabe: Boolean((p as Record<string, unknown> | null)?.sgb5_medigabe),
    sgb5_medigabe_taeglich: safeStr((p as Record<string, unknown> | null)?.sgb5_medigabe_taeglich),
    sgb5_ats: Boolean((p as Record<string, unknown> | null)?.sgb5_ats),
    pflegegrad: safeStr(p?.pflegegrad),
    beweglichkeit: (() => {
      const v = p?.beweglichkeit as unknown;
      if (Array.isArray(v)) return (v[0] as string) ?? '';
      if (typeof v === 'string' && v.startsWith('[')) {
        try { const parsed = JSON.parse(v); if (Array.isArray(parsed)) return (parsed[0] as string) ?? ''; } catch { /* ignore */ }
      }
      return typeof v === 'string' ? v : '';
    })(),
    einschraenkungen: safeArr(p?.einschraenkungen),
    sonstige_einschraenkungen: safeStr(p?.sonstige_einschraenkungen as unknown),
    besserung: safeStr(p?.besserung as unknown),
    sinneswahrnehmung: safeArr(p?.sinneswahrnehmung as unknown),
    geisteszustand: safeStr(p?.geisteszustand as unknown),
    schlafverhalten: safeStr(p?.schlafverhalten as unknown),
    grundpflege: safeStr(p?.grundpflege as unknown),
    hygiene: safeStr(p?.hygiene as unknown),
    ankleiden: safeStr(p?.ankleiden as unknown),
    essen: safeStr(p?.essen as unknown),
    urinkontrolle: safeStr(p?.urinkontrolle as unknown),
    stuhlkontrolle: safeStr(p?.stuhlkontrolle as unknown),
    wohnsituation: safeStr(p?.wohnsituation as unknown),
    wohnort: safeStr(p?.wohnort as unknown),
    wohnzustand: safeArr(p?.wohnzustand as unknown),
    haustiere: safeStr(p?.haustiere as unknown),
    haustiere_welche: safeArr(p?.haustiere_welche as unknown),
    einkaufsmoeglichkeiten: safeStr(p?.einkaufsmoeglichkeiten as unknown),
    charaktereigenschaften: safeArr(p?.charaktereigenschaften as unknown),
    ausgepraegtes_familienleben: Boolean(p?.ausgepraegtes_familienleben),
    bk_anrede: safeArr(p?.bk_anrede as unknown),
    bk_aktivitaeten: safeArr(p?.bk_aktivitaeten as unknown),
    bk_deutsch: safeStr(p?.bk_deutsch as unknown),
    bk_fuehrerschein: safeStr(p?.bk_fuehrerschein as unknown),
    bk_raucher: safeStr(p?.bk_raucher as unknown),
    hobbys: safeStr(p?.hobbys as unknown),
    umfeld: safeStr(p?.umfeld as unknown),
    sonstiges: safeStr(p?.sonstiges as unknown),
    freitext_sis_1: safeStr(p?.freitext_sis_1 as unknown),
    freitext_sis_2: safeStr(p?.freitext_sis_2 as unknown),
    freitext_sis_3: safeStr(p?.freitext_sis_3 as unknown),
    freitext_sis_4: safeStr(p?.freitext_sis_4 as unknown),
    freitext_sis_5: safeStr(p?.freitext_sis_5 as unknown),
    freitext_sis_6: safeStr(p?.freitext_sis_6 as unknown),
    freitext_sis_7: safeStr(p?.freitext_sis_7 as unknown),
    freitext_sis_8: safeStr(p?.freitext_sis_8 as unknown),
    freitext_sis_9: safeStr(p?.freitext_sis_9 as unknown),
    sektionen_aktiv: parseSektionenAktiv((p as Record<string, unknown> | null)?.sektionen_aktiv),
  });
}

export function PbProfileEdit({ user, onClose, onSaved, dark, embedded = false, careRecipient = true }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const optLabel = useOptionLabel();
  const isMobile = useIsMobile();
  const [f, setF] = useState<PbFormState>(() => userToForm(user));
  const [opts, setOpts] = useState<Record<string, V1ProfileOption[]> | null>(null);
  const [optsError, setOptsError] = useState<string | null>(null);
  const [active, setActive] = useState<string>('stammdaten');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Array<keyof PbFormState>>([]);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const jumpingRef = useRef(false);
  const profileId = user ? primaryProfile(user)?.id ?? null : null;
  // A9: schlanke Sicht, solange die Person kein Pflegeempfänger ist und der
  // Schalter im Kopfblock nicht aktiviert wurde.
  const slim = !careRecipient && !f.is_pflegeempfaenger;

  // WP5: derive per-section permissions from the live permissions context.
  // deriveSectionPerms maps hasPermission calls to the four gated sections.
  const { hasPermission } = usePermissions();
  const sectionPerms = useMemo(() => deriveSectionPerms(hasPermission), [hasPermission]);

  // load profile_options (admin endpoint) — DB-real dropdown values
  useEffect(() => {
    let alive = true;
    v1ProfileOptions.grouped()
      .then(grouped => { if (alive) setOpts(grouped); })
      .catch(e => { if (alive) setOptsError(e instanceof Error ? e.message : tr('pflege.edit.error_options')); });
    return () => { alive = false; };
  }, []);

  // "Erstellt von" zeigte bisher die rohe Admin-ID. Derselbe Weg wie bei der
  // Zustaendigkeits-Anzeige in PbSidebar: Adminliste holen, Namen einsetzen.
  useEffect(() => {
    const createdById = Number(user?.created_by ?? 0);
    if (!createdById) return;
    let alive = true;
    void v1Admins.list()
      .then(list => {
        if (!alive) return;
        const a = list.find(x => x.id === createdById);
        if (!a) return;
        const name = [a.vorname, a.nachname].filter(Boolean).join(' ') || a.email || '';
        if (name) setF(prev => ({ ...prev, created_by_name: name }));
      })
      .catch(() => {/* faellt auf die ID zurueck */});
    return () => { alive = false; };
  }, [user?.created_by]);

  // Startdatum wird eingebettet in der PbSidebar inline gespeichert; deren
  // onSaved aktualisiert `user` → Formularstand nachziehen, damit ein
  // späterer „Profil speichern" nicht den alten Wert zurückschreibt.
  const userStartdatum = user?.startdatum;
  useEffect(() => {
    const next = userStartdatum == null ? '' : String(userStartdatum);
    setF(prev => (prev.startdatum === next ? prev : { ...prev, startdatum: next }));
  }, [userStartdatum]);

  const set = useCallback(<K extends keyof PbFormState>(key: K, val: PbFormState[K]) => {
    setF(prev => ({ ...prev, [key]: val }));
  }, []);

  // Welcher Container scrollt? Desktop = innerer mainRef (Two-Pane-Layout
  // mit sticky Sidebar). Mobile = das outer <main class="app-main"> der
  // App-Shell, weil wir auf Mobile auf Single-Scroll umgestellt haben.
  const getScrollContainer = useCallback((): HTMLElement | null => {
    if (!isMobile) return mainRef.current;
    return document.querySelector('main.app-main');
  }, [isMobile]);

  // Sticky-Offset oben: Auf Mobile sitzt die Section-Nav-Pillsleiste
  // direkt unter der App-Navbar (sticky top:0 innerhalb des outer <main>),
  // ist etwa 50px hoch — damit beim Spy/Jump das aktive Pill mit der
  // tatsaechlich sichtbaren Section uebereinstimmt, ziehen wir die Hoehe
  // vom Scroll-Trigger ab.
  const STICKY_OFFSET = isMobile ? 70 : 90;

  // scroll-spy across all section ids (only visible ones)
  useEffect(() => {
    const main = getScrollContainer();
    if (!main) return;
    const onScroll = () => {
      if (jumpingRef.current) return;
      const ids = SECTIONS_NAV.flatMap(g => g.items.map(i => i.id));
      const mTop = main.getBoundingClientRect().top;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - mTop <= STICKY_OFFSET) current = id;
      }
      setActive(current);
    };
    main.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => main.removeEventListener('scroll', onScroll);
  }, [getScrollContainer, STICKY_OFFSET]);

  const jump = (id: string) => {
    const main = getScrollContainer();
    const el = document.getElementById(id);
    if (!main || !el) return;
    jumpingRef.current = true;
    setActive(id);
    // Section-Top relativ zum Scroll-Container holen, dann sticky-Hoehe
    // abziehen, damit der Section-Header nicht hinter der Pills-Leiste
    // verschwindet.
    const top = el.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop - STICKY_OFFSET + 12;
    main.scrollTo({ top, behavior: 'smooth' });
    setTimeout(() => { jumpingRef.current = false; }, 600);

    // Aktive Pill in der horizontalen Pill-Leiste mittig nachziehen —
    // nur scrollLeft der <nav>, NICHT scrollIntoView verwenden, weil das
    // mit dem smooth-scroll von main konkurriert und main.scrollTo killt.
    if (isMobile) {
      const pill = document.querySelector<HTMLElement>(`[data-pb-nav="${id}"]`);
      const nav = pill?.parentElement;
      if (pill && nav) {
        const pRect = pill.getBoundingClientRect();
        const nRect = nav.getBoundingClientRect();
        const targetLeft = pRect.left - nRect.left + nav.scrollLeft - (nRect.width - pRect.width) / 2;
        nav.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
      }
    }
  };

  const save = async () => {
    if (saving) return;
    // WP4: validate required fields before hitting the API
    // Aktivierte Sektionen (z. B. SIS) müssen komplett ausgefüllt sein; nicht
    // bearbeitbare Sektionen (Rechte, Freitexte nur lesend) zählen nicht.
    const vr = validateProfile(f, formRequiredContext({ slim, perms: sectionPerms, freitexteReadOnly: true }));
    if (vr.missing.length > 0) {
      setValidationErrors(vr.missing);
      setSaveError(tr('pflege.familienkonto.detail.missing_fields_error', { fields: missingFieldLabels(vr.missing, tr) }));
      if (vr.firstSection) jump(vr.firstSection);
      return;
    }
    setValidationErrors([]);
    if (!user?.id) {
      // "Neuer Klient" flow — needs POST /admin/user first; not yet implemented.
      setSaveError(tr('pflege.edit.new_not_impl'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // WP5: strip fields belonging to sections the admin cannot write.
      // bank fields live in USER_FIELDS; versicherung fields in PROFILE_FIELDS;
      // status fields are in USER_FIELDS.
      const BANK_USER_FIELDS = ['kontoinhaber', 'bankname', 'iban'] as const;
      const STATUS_USER_FIELDS = ['todestag', 'invisible', 'status', 'konto', 'startdatum'] as const;
      const VERSICHERUNG_PROFILE_FIELDS = [
        'krankenkasse', 'krankenkasse_typ', 'kranken_vnr', 'sozial_vnr', 'renten_vnr',
        'steuer_id', 'lbnr', 'sozialamt_aufstockung',
      ] as const;
      // Felder, die in dieser Ansicht gar nicht (editierbar) angezeigt werden,
      // nicht mit dem beim Öffnen geladenen Stand zurückschreiben — sonst
      // überschreibt ein Save Änderungen, die zwischenzeitlich anderswo
      // gemacht wurden. status/konto stehen nur in der Standalone-Kopfkarte,
      // die Freitexte (06) sind hier nur lesend. Das Startdatum ist auch
      // eingebettet editierbar (inline in der PbSidebar) und wird hier mit dem
      // aktuellen Stand mitgespeichert (s. Sync-Effekt auf user.startdatum).
      // Ebenso die Felder deaktivierter Sektionen (02–07): sie bleiben in der
      // DB unverändert, bis die Sektion wieder eingeschaltet wird.
      const HIDDEN_FIELDS = new Set<string>([
        ...(embedded ? ['status', 'konto'] : []),
        'hobbys', 'umfeld', 'sonstiges',
        // Impfstatus wird nicht mehr angezeigt (Ticket cmv58ofmr0009ry01i0wefm94).
        'impfstatus',
        // Kategorie (Badge) steht in Wunsch-BK und ist bei inaktiver Sektion
        // nicht sichtbar.
        ...(isSectionActive(f, 'wunsch') ? [] : ['badge']),
        ...inactiveSectionFields(f),
      ]);

      const effectiveUserFields = (USER_FIELDS as readonly (keyof PbFormState)[]).filter(k => {
        if (HIDDEN_FIELDS.has(k)) return false;
        if (!sectionPerms.bank.canWrite && (BANK_USER_FIELDS as readonly string[]).includes(k)) return false;
        if (!sectionPerms.status.canWrite && (STATUS_USER_FIELDS as readonly string[]).includes(k)) return false;
        return true;
      });
      const effectiveProfileFields = (PROFILE_FIELDS as readonly (keyof PbFormState)[]).filter(k => {
        if (HIDDEN_FIELDS.has(k)) return false;
        if (!sectionPerms.versicherung.canWrite && (VERSICHERUNG_PROFILE_FIELDS as readonly string[]).includes(k)) return false;
        return true;
      });

      const userPayload = pickPayload(f, effectiveUserFields);
      // Leeres Startdatum als NULL senden — '' ist kein gültiger DATE-Wert.
      if (userPayload.startdatum === '') userPayload.startdatum = null;
      const profilePayload = pickPayload(f, effectiveProfileFields);

      // PUT user (always) + PUT profile (only if we know the profile id).
      // Run sequentially: the legacy controller re-derives group_id from
      // assigned_to inside updateUser, so we want that done before we look
      // at the response.
      await v1Users.update(user.id, userPayload);
      if (profileId != null && Object.keys(profilePayload).length > 0) {
        await v1Profiles.update(profileId, profilePayload);
      }
      // Mehrfach-Rolle läuft nur über v2 (v1 kennt profiltypen nicht).
      if (profileId != null && Array.isArray(f.profiltypen)) {
        await v2Members.update(profileId, { profiltypen: selectedRoles(f) } as unknown as FamilyMemberInput);
      }

      // Eine eingetragene, noch nicht gespeicherte Vertragsanpassung (09)
      // mitspeichern — sonst ginge ein neuer Vertragspreis beim Klick auf
      // „Profil speichern" stillschweigend verloren. Ein Fehler dort macht
      // den Profil-Save nicht rückgängig, wird aber deutlich gemeldet.
      let vertragError: string | null = null;
      try {
        await flushPendingVertrag(user.id);
      } catch (e) {
        vertragError = e instanceof Error ? e.message : tr('pflege.edit.save_failed');
      }

      // Vollständigen Stand neu laden (inkl. profiles/prices/boni): die PUT-
      // Antworten sind nackte Models — damit weitergearbeitet, fehlten nach
      // einem Tab-Wechsel Vertragsdaten und bei Folge-Saves sogar die ID.
      const fresh = await v1Users.get(user.id);
      onSaved?.(fresh);
      setF(prev => ({ ...userToForm(fresh), created_by_name: prev.created_by_name }));

      const now = new Date();
      setSavedAt(formatTime(now));
      if (vertragError) {
        setSaveError(tr('pflege.form.vertrag.pending_failed', { message: vertragError }));
        jump('vertrag');
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setSaving(false);
    }
  };
  const reset = () => { setF(userToForm(user)); setValidationErrors([]); };

  /**
   * Termine-Sektion (08): speichert `anamnese_termin_am`/`beratungsgespraech_am`
   * sofort über PUT /v2/admin/profiles/{id} — wie das Muster im PhaseRulesBlock
   * (s. dessen `save()`), NICHT über den großen Profildaten-Save-Button, damit
   * der Kalendersync (AnamneseTerminSync/BeratungsgespraechTerminSync) greift
   * und ein Klick auf „Profil speichern" mit veraltetem Formularstand den
   * gerade gesetzten Termin nicht überschreibt.
   */
  const saveTermin = useCallback(async (
    patch: Partial<Record<TerminField, string | null>>,
  ) => {
    if (!profileId || Object.keys(patch).length === 0) return;
    // Datum + Uhrzeit in EINEM Request — ein Kalender-Sync, kein Zwischenstand.
    await v2Members.update(profileId, patch as unknown as FamilyMemberInput);
    setF(prev => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(patch)) (next as Record<string, unknown>)[k] = v ?? '';
      return next;
    });
    if (user) onSaved?.({ ...user, ...patch } as V1User);
  }, [profileId, user, onSaved]);

  const name = [f.titel, f.vorname, f.nachname].filter(Boolean).join(' ') || tr('pflege.detail.new_profile');
  const initials = ((f.vorname?.[0] ?? '') + (f.nachname?.[0] ?? '')).toUpperCase() || 'PB';
  // Capture "now" once per mount to satisfy React's purity rule — age in years only
  // needs to be fresh per page-open, not per re-render.
  const [nowMs] = useState(() => Date.now());
  const age = useMemo(() => {
    if (!f.geburtsdatum) return null;
    const d = new Date(f.geburtsdatum);
    if (isNaN(d.getTime())) return null;
    return Math.floor((nowMs - d.getTime()) / 31557600000);
  }, [f.geburtsdatum, nowMs]);
  const meta = [age != null ? `${age} ${tr('pflege.detail.years')}` : null, f.ort, optLabel(lookup(HARDCODED.konto, f.konto) ?? '')].filter(Boolean).join(' · ');
  const statusLabel = lookup(HARDCODED.status, f.status) ?? 'Ausstehend';

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      // Mobile gives up the inner-scroll pattern and flows into the outer
      // page scroll; desktop keeps the two-pane (sidebar + scroll-area)
      // layout intact so the section nav stays sticky over a tall form.
      height: isMobile ? 'auto' : '100%',
      minHeight: 0,
    }}>

      {/* Standalone-only chrome: breadcrumb + back button. When embedded
          in PbDetailPage the wrapper renders those already. */}
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, flexShrink: 0 }}>
          <button onClick={onClose} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8,
            padding: '6px 12px', cursor: 'pointer', color: t.text,
            fontSize: 13, fontFamily: 'Outfit,sans-serif',
          }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>‹</span> {tr('pflege.detail.back')}
          </button>
          <div style={{ fontSize: 13, color: t.muted }}>
            {tr('nav.menu.pflege')} <span style={{ margin: '0 6px' }}>/</span>
            <span style={{ color: t.heading, fontWeight: 600 }}>{tr('pflege.edit.breadcrumb_edit')}</span>
          </div>
        </div>
      )}

      {optsError && (
        <Card dark={dark} style={{ padding: 12, marginBottom: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>
            {tr('pflege.edit.error_options_card')} ({optsError})
          </div>
        </Card>
      )}

      {/* Scroll area — desktop scrolls internally so the section-nav
          stays anchored; mobile lets the outer <main> scroll. */}
      <div ref={mainRef} style={{
        flex: isMobile ? undefined : 1,
        overflowY: isMobile ? 'visible' : 'auto',
        minHeight: 0,
        paddingRight: isMobile ? 0 : 4,
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', paddingBottom: 24 }}>

          {/* Standalone-only summary card. Embedded mode delegates the
              header to the PbSidebar in the outer wrapper. */}
          {!embedded && (
            <Card dark={dark} style={{ overflow: 'hidden', padding: 0 }}>
              <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                <Avatar name={initials.split('').join(' ')} userId={user?.id ?? null} size={60} dark={dark} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: t.heading }}>{name}</h2>
                    <Badge dark={dark} tone={statusLabel === 'Aktiv' ? 'success' : statusLabel === 'Pausiert' ? 'neutral' : 'warning'}>
                      {optLabel(statusLabel)}
                    </Badge>
                    {f.pflegegrad && (
                      <Badge dark={dark} tone="brand">{optLabel(lookupOptName(f.pflegegrad, opts, 'pflegegrad') ?? f.pflegegrad)}</Badge>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: t.muted, marginTop: 4 }}>{meta || tr('pflege.cares.th.care_recipient')}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: t.muted, marginBottom: 4 }}>ID</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{user?.id ? `#${user.id}` : '—'}</div>
                </div>
              </div>
              <div style={{
                borderTop: `1px solid ${t.borderSoft}`, background: t.cardMuted,
                padding: isMobile ? '14px 16px' : '16px 24px',
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                gap: isMobile ? 14 : 20,
              }}>
                <HeaderField dark={dark} label={tr('pflege.edit.header.status')}>
                  <Select dark={dark} value={f.status} onChange={v => set('status', v)} options={HARDCODED.status} placeholder="—" />
                </HeaderField>
                <HeaderField dark={dark} label={tr('pflege.edit.header.konto')}>
                  <Select dark={dark} value={f.konto} onChange={v => set('konto', v)} options={HARDCODED.konto} placeholder="—" />
                </HeaderField>
                <HeaderField dark={dark} label={tr('pflege.edit.header.startdatum')}>
                  <Text dark={dark} value={f.startdatum} onChange={v => set('startdatum', v)} type="date" />
                </HeaderField>
              </div>
            </Card>
          )}

          {/* "Phasen-Daten"-Card entfernt — die Bedingungsfelder des
              Phasen-Regelsystems werden jetzt im Phasen-Block (PbPhaseBar)
              über den PhaseRulesBlock gepflegt. */}

          {/* Sticky nav + form grid. On mobile the 220px section nav
              collapses to a horizontal pill scroller above the form so
              the form gets the full width. */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '220px 1fr',
            gap: isMobile ? 14 : 28,
            marginTop: embedded ? 0 : 20, alignItems: 'start',
          }}>
            <SectionNav active={active} onJump={jump} dark={dark} isMobile={isMobile} sectionPerms={sectionPerms} slim={slim} />
            <FormSections f={f} set={set} opts={opts} dark={dark} showProfilHead invalidFields={validationErrors} sectionPerms={sectionPerms} slim={slim} freitexteReadOnly profileId={profileId} onSaveTermin={saveTermin} priceUser={user}
              contentTranslations={user?.content_translations ?? null}
              translationIds={{ profile: profileId, user: user?.id ?? null }} />
          </div>
        </div>
      </div>

      {/* Sticky save bar. On mobile the status line moves above the buttons
          and the buttons stretch to 50/50 so „Profil speichern" stays
          tappable with the thumb at the screen edge. */}
      <div style={{
        borderTop: `1px solid ${t.border}`, background: t.card,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        gap: isMobile ? 10 : 0,
        padding: isMobile ? '12px 12px' : '14px 8px',
        flexShrink: 0, marginTop: 14,
      }}>
        <div style={{ fontSize: 12.5, color: saveError ? '#b42318' : t.muted }}>
          {saveError
            ? tr('pflege.edit.error_prefix', { msg: saveError })
            : saving
              ? tr('pflege.edit.saving')
              : savedAt
                ? tr('pflege.edit.saved_at', { time: savedAt })
                : tr('pflege.edit.save_hint')}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            variant="secondary" dark={dark} onClick={reset}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >{tr('pflege.edit.reset')}</Button>
          <Button
            variant="primary" dark={dark} onClick={save} icon={<Icon.Check c="#fff" />}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >
            {saving ? tr('pflege.edit.saving') : tr('pflege.edit.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Header sub-field (label + control) ───────────────────── */
function HeaderField({ label, children, dark }: { label: string; children: React.ReactNode; dark: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: t.muted,
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>{label}</span>
      {children}
    </div>
  );
}

/* ─── Sticky section nav with scroll-spy ───────────────────── */
function SectionNav({
  active, onJump, dark, isMobile = false, sectionPerms, slim = false,
}: {
  active: string;
  onJump: (id: string) => void;
  dark: boolean;
  isMobile?: boolean;
  sectionPerms?: import('./sections').SectionPerms;
  /** A9: nur Profil + Stammdaten listen. */
  slim?: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  // WP5: section ids that should be hidden because the admin lacks canRead.
  const SECTION_PERM_MAP: Record<string, keyof import('./sections').SectionPerms> = {
    vertrag:      'vertrag',
    versicherung: 'versicherung',
    bank:         'bank',
    status:       'status',
  };
  const isHidden = (id: string): boolean => {
    if (slim && !SLIM_SECTION_IDS.includes(id)) return true;
    const permKey = SECTION_PERM_MAP[id];
    if (!permKey || !sectionPerms) return false;
    return !sectionPerms[permKey].canRead;
  };

  // Filtered nav groups: remove hidden sections, drop empty groups.
  const visibleNav = SECTIONS_NAV
    .map(grp => ({ ...grp, items: grp.items.filter(it => !isHidden(it.id)) }))
    .filter(grp => grp.items.length > 0);

  // Scroll-affordance for the mobile horizontal pill bar: show a right-edge
  // fade + chevron while more pills exist past the right edge, so it's obvious
  // the nav scrolls. Hooks declared unconditionally (desktop just ignores them).
  const navScrollRef = useRef<HTMLElement | null>(null);
  const [showRightFade, setShowRightFade] = useState(false);
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const update = () => setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [isMobile, active]);

  // Mobile: flatten the grouped nav into a single horizontal pill scroller
  // pinned below the page header. Section headlines stay implicit (the form
  // sections themselves carry their titles), and the active pill scrolls
  // into view via scroll-spy behaviour in the parent.
  if (isMobile) {
    type NavItem = { id: string; num: string; labelKey: string };
    const flat: NavItem[] = visibleNav.flatMap(grp => grp.items as readonly NavItem[]);
    return (
      // Sticky wrapper hosts the scroller + a right-edge fade overlay that
      // signals there are more sections to the right.
      <div style={{ position: 'sticky', top: 0, zIndex: 10, borderRadius: 10 }}>
        <nav
          ref={navScrollRef as React.RefObject<HTMLElement>}
          className="pb-section-nav"
          style={{
            display: 'flex', gap: 6, padding: 4,
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
            boxShadow: dark ? 'none' : '0 2px 6px rgba(16,24,40,0.08)',
          }}>
          {flat.map(it => {
            const on = active === it.id;
            return (
              <button
                key={it.id}
                type="button"
                data-pb-nav={it.id}
                onClick={() => onJump(it.id)}
                style={{
                  border: 'none', cursor: 'pointer',
                  padding: '8px 12px', borderRadius: 8,
                  background: on ? BRAND_LIGHT : 'transparent',
                  color: on ? BRAND_DARK : t.text,
                  fontSize: 12.5, fontWeight: on ? 600 : 500,
                  fontFamily: 'Outfit,sans-serif',
                  flexShrink: 0, whiteSpace: 'nowrap', minHeight: 38,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.15s, color 0.15s',
                }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: on ? BRAND : t.muted }}>{it.num}</span>
                {tr(it.labelKey)}
              </button>
            );
          })}
        </nav>
        {showRightFade && (
          <div aria-hidden style={{
            position: 'absolute', top: 1, right: 1, bottom: 1, width: 44,
            borderRadius: '0 10px 10px 0', pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 7,
            background: `linear-gradient(to right, transparent, ${t.card} 72%)`,
          }}>
            <span style={{ color: BRAND, fontSize: 17, fontWeight: 700, lineHeight: 1 }}>›</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <nav className="pb-section-nav" style={{
      position: 'sticky', top: 0, alignSelf: 'start',
      display: 'flex', flexDirection: 'column', gap: 18,
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 80px)',
      paddingRight: 4,
    }}>
      {visibleNav.map(grp => (
        <div key={grp.groupKey}>
          <div style={{
            fontSize: 10.5, fontWeight: 700, color: t.muted,
            textTransform: 'uppercase', letterSpacing: '0.07em',
            marginBottom: 8, paddingLeft: 12,
          }}>{tr(grp.groupKey)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {grp.items.map(it => {
              const on = active === it.id;
              return (
                <button key={it.id} type="button" onClick={() => onJump(it.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, border: 'none', cursor: 'pointer',
                  borderRadius: 8, padding: '8px 12px', textAlign: 'left', fontFamily: 'Outfit,sans-serif',
                  background: on ? BRAND_LIGHT : 'transparent', color: on ? BRAND_DARK : t.text,
                  fontSize: 13, fontWeight: on ? 600 : 500, transition: 'all 0.12s',
                }}
                  onMouseEnter={e => { if (!on) (e.currentTarget as HTMLButtonElement).style.background = t.hover; }}
                  onMouseLeave={e => { if (!on) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: on ? BRAND : t.muted, width: 16, flexShrink: 0 }}>{it.num}</span>
                  {tr(it.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

/* ─── helpers ──────────────────────────────────────────────── */
function lookup(opts: { value: string; label: string }[], v: string | undefined): string | null {
  if (v == null) return null;
  return opts.find(o => o.value === v)?.label ?? null;
}
function lookupOptName(
  v: string | undefined,
  opts: Record<string, V1ProfileOption[]> | null,
  field: string,
): string | null {
  if (v == null || !opts) return null;
  return opts[field]?.find(o => String(o.id) === v)?.value ?? null;
}

// Re-use the Avatar component visually if needed elsewhere (keeps import tree-shakable)
void Avatar;
